/**
 * Appearance settings a CV carries next to its template: accent colour,
 * typeface, density, page size, section order and skill levels.
 *
 * They live in `cvData.design` (stored in cv_requests.parsed_data), so the
 * preview, the PDF export, guest downloads and public pages all get them with
 * the CV itself. Every value is optional: a CV without `design` renders
 * exactly as before, in the template's own colours and type.
 *
 * Kept free of Vue/Nuxt imports: the server (server/utils/cvDesignCss.ts)
 * and the editor read the same lists.
 */

export interface CvAccentPreset {
  id: string
  hex: string
}

/** Swatches the editor offers. The first is the green the templates ship with. */
export const CV_ACCENT_PRESETS: readonly CvAccentPreset[] = [
  { id: 'green', hex: '#15803d' },
  { id: 'teal', hex: '#0f766e' },
  { id: 'blue', hex: '#1d4ed8' },
  { id: 'navy', hex: '#1e3a8a' },
  { id: 'purple', hex: '#6d28d9' },
  { id: 'burgundy', hex: '#9f1239' },
  { id: 'orange', hex: '#c2410c' },
  { id: 'graphite', hex: '#374151' },
]

export const CV_FONT_IDS = ['default', 'dm-sans', 'inter', 'lato', 'merriweather', 'source-serif'] as const
export type CvFontId = typeof CV_FONT_IDS[number]

export interface CvFontOption {
  id: CvFontId
  /** Shown in the picker, in its own face. */
  label: string
  serif: boolean
  /** CSS stack for the editor's sample; the PDF uses the embedded face (server/utils/pdfFontAssets.ts). */
  previewStack: string
}

export const CV_FONTS: readonly CvFontOption[] = [
  { id: 'default', label: 'Template', serif: false, previewStack: 'inherit' },
  { id: 'dm-sans', label: 'DM Sans', serif: false, previewStack: "'DM Sans', Arial, sans-serif" },
  { id: 'inter', label: 'Inter', serif: false, previewStack: 'Inter, Arial, sans-serif' },
  { id: 'lato', label: 'Lato', serif: false, previewStack: 'Lato, Arial, sans-serif' },
  { id: 'merriweather', label: 'Merriweather', serif: true, previewStack: 'Merriweather, Georgia, serif' },
  { id: 'source-serif', label: 'Source Serif', serif: true, previewStack: "'Source Serif 4', Georgia, serif" },
]

export const CV_DENSITIES = ['compact', 'normal', 'spacious'] as const
export type CvDensity = typeof CV_DENSITIES[number]

/**
 * How much each density scales the sheet's contents (type and spacing
 * together, via CSS zoom). Compact is what gets a CV that runs a little long
 * onto one page; spacious fills a short one.
 */
export const CV_DENSITY_ZOOM: Record<CvDensity, number> = {
  compact: 0.9,
  normal: 1,
  spacious: 1.07,
}

export const CV_PAGE_SIZES = ['a4', 'letter'] as const
export type CvPageSize = typeof CV_PAGE_SIZES[number]

/** Page dimensions in millimetres. */
export const CV_PAGE_MM: Record<CvPageSize, { width: number; height: number }> = {
  a4: { width: 210, height: 297 },
  letter: { width: 215.9, height: 279.4 },
}

/**
 * Sections a CV can reorder. The header (name, photo, contact) always comes
 * first and the consent clause always last. Two-column templates reorder each
 * column on its own: the order is relative, not a slot per section.
 */
export const CV_SECTION_IDS = [
  'about',
  'experience',
  'education',
  'skills',
  'languages',
  'certificates',
  'custom',
  'links',
  'hobbies',
] as const
export type CvSectionId = typeof CV_SECTION_IDS[number]

/** Skill level scale shown as dots: 1 (basic) … 5 (expert). */
export const SKILL_LEVEL_MAX = 5

export interface CVDesign {
  /** #rrggbb. Absent: the template's own colour. */
  accentColor?: string
  font?: CvFontId
  density?: CvDensity
  pageSize?: CvPageSize
  /** Absent or empty: the template's own order. */
  sectionOrder?: CvSectionId[]
  /** Draw skill dots and language bars from the levels the CV carries. */
  showLevels?: boolean
}

/** A fully resolved design: what the renderer works from. */
export interface ResolvedCVDesign {
  accentColor: string | null
  font: CvFontId
  density: CvDensity
  pageSize: CvPageSize
  sectionOrder: CvSectionId[]
  showLevels: boolean
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i

function includes<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value)
}

/**
 * Reads `cvData.design` from anywhere (database JSON, request bodies) into
 * known values only. The colour goes into a stylesheet, so anything but a
 * plain #rrggbb is dropped rather than passed on.
 */
export function resolveDesign(input: unknown): ResolvedCVDesign {
  const design = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>
  const accent = typeof design.accentColor === 'string' && HEX_COLOR.test(design.accentColor.trim())
    ? design.accentColor.trim().toLowerCase()
    : null
  const order = Array.isArray(design.sectionOrder)
    ? design.sectionOrder.filter((id, index, all): id is CvSectionId => includes(CV_SECTION_IDS, id) && all.indexOf(id) === index)
    : []
  return {
    accentColor: accent,
    font: includes(CV_FONT_IDS, design.font) ? design.font : 'default',
    density: includes(CV_DENSITIES, design.density) ? design.density : 'normal',
    pageSize: includes(CV_PAGE_SIZES, design.pageSize) ? design.pageSize : 'a4',
    sectionOrder: order,
    showLevels: design.showLevels === true,
  }
}

/** The section order the editor starts from: every section, in the default order. */
export function fullSectionOrder(order: readonly CvSectionId[] | undefined): CvSectionId[] {
  const known = (order ?? []).filter(id => includes(CV_SECTION_IDS, id))
  return [...new Set([...known, ...CV_SECTION_IDS])]
}

/** Clamps a stored skill level to 0 (none) … SKILL_LEVEL_MAX. */
export function clampSkillLevel(value: unknown): number {
  const level = typeof value === 'number' ? Math.round(value) : Number.NaN
  if (!Number.isFinite(level) || level < 1) return 0
  return Math.min(level, SKILL_LEVEL_MAX)
}

/**
 * CEFR levels and the usual words for them (en/cs/sk/de/pl), as a bar length.
 * Anything unrecognised returns 0 and the template shows the text alone:
 * a bar that guesses would say something the candidate never wrote.
 */
export function languageLevelPercent(level?: string): number {
  const value = (level || '').toLowerCase()
  if (!value) return 0
  const cefr = value.match(/\b([abc])\s?([12])\b/)
  if (cefr) return ({ a1: 20, a2: 35, b1: 50, b2: 65, c1: 85, c2: 100 } as Record<string, number>)[`${cefr[1]}${cefr[2]}`] ?? 0
  const words: [RegExp, number][] = [
    // Most specific first: "limited working proficiency", "pre-intermediate".
    [/limited|elementary|pre.?intermediate/, 35],
    [/upper.?intermediate/, 65],
    [/native|mother|rodil|materin|mateřs|muttersprach|ojczyst|bilingual|bilingv/, 100],
    [/fluent|plynul|flie(ss|ß)end|biegł|proficien/, 90],
    [/advanced|pokroč|pokroc|fortgeschritten|zaawansowan|professional/, 80],
    [/intermediate|středn|stredn|konverza|conversational|mittel|komunikat/, 55],
    [/basic|beginner|základ|zaklad|začáteč|zaciatoc|grundkenntnis|anfänger|podstaw/, 25],
  ]
  return words.find(([pattern]) => pattern.test(value))?.[1] ?? 0
}

/**
 * The fixed scale a language level is picked from, top first. The editor
 * offers exactly these and the AI fills in exactly these, so the bar never has
 * to guess from free text. CEFR codes print as they are (recruiters read them
 * in any language); `native` prints in the CV's own language.
 *
 * Older CVs keep whatever text they had: it still prints, and it gets a bar
 * only when languageLevelPercent can read it.
 */
export const LANGUAGE_LEVELS = ['native', 'C2', 'C1', 'B2', 'B1', 'A2', 'A1'] as const
export type LanguageLevel = typeof LANGUAGE_LEVELS[number]

const NATIVE_WORDS = /native|mother|rodil|materin|mateřs|muttersprach|ojczyst|bilingual|bilingv/

/**
 * A stored level as a step of the scale when it clearly is one: a CEFR code in
 * any spelling ("c1", "B2 – upper") or a word for native. Anything else ("fluent",
 * "conversational") is left to the person, so this returns ''.
 */
export function toLanguageLevel(value?: string): LanguageLevel | '' {
  const text = (value || '').trim()
  if (!text) return ''
  if ((LANGUAGE_LEVELS as readonly string[]).includes(text)) return text as LanguageLevel
  const lower = text.toLowerCase()
  if (NATIVE_WORDS.test(lower)) return 'native'
  const cefr = lower.match(/\b([abc])\s?([12])\b/)
  return cefr ? (`${cefr[1]!.toUpperCase()}${cefr[2]}` as LanguageLevel) : ''
}

/** What the CV prints for a level: the CV's word for native, a CEFR code as is, older free text unchanged. */
export function languageLevelText(value: string | undefined, nativeLabel: string): string {
  const text = (value || '').trim()
  return text === 'native' ? nativeLabel : text
}
